<template>
    <v-dialog
        :model-value="modelValue"
        max-width="640"
        scrollable
        @update:model-value="$emit('update:modelValue', $event)"
    >
        <v-card rounded="lg" class="corporate-dialog">
            <v-card-title class="corporate-dialog__header">
                <div>
                    <h2 class="corporate-dialog__title">{{ labels.changeOrg }}</h2>
                    <p v-if="user" class="corporate-dialog__subtitle">
                        {{ user.name }} · {{ user.email }}
                    </p>
                </div>
            </v-card-title>
            <v-card-text class="corporate-dialog__body">
                <section class="corporate-dialog__section">
                    <p class="corporate-dialog__section-label">{{ sectionDataLabel }}</p>
                    <div class="corporate-dialog__section-body">
                        <v-select
                            v-if="boardOptions && boardOptions.length"
                            v-model="form.boardId"
                            :items="boardOptions"
                            item-title="text"
                            item-value="value"
                            :label="labels.board"
                            variant="outlined"
                            density="compact"
                            clearable
                            class="weeduc-admin-v-field"
                            hide-details="auto"
                        />
                        <div class="corporate-dialog__row">
                            <v-select
                                v-model="form.sectorId"
                                :items="filteredSectorOptions"
                                item-title="text"
                                item-value="value"
                                :label="labels.sector"
                                variant="outlined"
                                density="compact"
                                clearable
                                class="weeduc-admin-v-field"
                                hide-details="auto"
                            />
                            <v-select
                                v-model="form.roleId"
                                :items="filteredRoleOptions"
                                item-title="text"
                                item-value="value"
                                :label="labels.role"
                                variant="outlined"
                                density="compact"
                                clearable
                                class="weeduc-admin-v-field"
                                hide-details="auto"
                            />
                        </div>
                        <v-select
                            v-model="form.managerUserId"
                            :items="managerOptions"
                            item-title="text"
                            item-value="value"
                            :label="labels.manager"
                            variant="outlined"
                            density="compact"
                            clearable
                            class="weeduc-admin-v-field"
                            hide-details="auto"
                        />
                        <div class="corporate-dialog__row">
                            <v-text-field
                                v-model="form.admissionDate"
                                :label="labels.admissionDate"
                                type="date"
                                variant="outlined"
                                density="compact"
                                class="weeduc-admin-v-field"
                                hide-details="auto"
                            />
                            <v-text-field
                                v-model="form.internalCode"
                                :label="labels.internalCode"
                                variant="outlined"
                                density="compact"
                                class="weeduc-admin-v-field"
                                hide-details="auto"
                            />
                        </div>
                        <v-select
                            v-model="form.status"
                            :items="statusOptions"
                            item-title="text"
                            item-value="value"
                            :label="labels.status"
                            variant="outlined"
                            density="compact"
                            class="weeduc-admin-v-field"
                            hide-details="auto"
                        />
                    </div>
                </section>
            </v-card-text>
            <v-card-actions class="corporate-dialog__actions">
                <v-spacer />
                <v-btn variant="text" class="text-none" @click="$emit('update:modelValue', false)">
                    {{ common.cancel }}
                </v-btn>
                <v-btn
                    color="primary"
                    variant="flat"
                    rounded="lg"
                    class="text-none"
                    :loading="saving"
                    @click="submit"
                >
                    {{ common.save }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { corporateI18n } from '@/i18n/mixins';

export default {
    name: 'MoveUserDialog',
    mixins: [corporateI18n],
    props: {
        modelValue: Boolean,
        user: Object,
        labels: Object,
        common: Object,
        boardOptions: { type: Array, default: () => [] },
        sectorOptions: { type: Array, default: () => [] },
        roleOptions: { type: Array, default: () => [] },
        managerOptions: { type: Array, default: () => [] },
        sectorsRaw: { type: Array, default: () => [] },
        rolesRaw: { type: Array, default: () => [] },
        saving: Boolean,
    },
    emits: ['update:modelValue', 'submit'],
    data() {
        return {
            form: {
                boardId: null,
                sectorId: null,
                roleId: null,
                managerUserId: null,
                admissionDate: null,
                internalCode: '',
                status: 'active',
            },
        };
    },
    computed: {
        statusOptions() {
            return [
                { text: this.labels.active, value: 'active' },
                { text: this.labels.inactive, value: 'inactive' },
            ];
        },
        filteredSectorOptions() {
            if (!this.boardOptions?.length || !this.form.boardId) return this.sectorOptions;
            const ids = this.sectorsRaw
                .filter((s) => s.parentId === this.form.boardId)
                .map((s) => s.id);
            return this.sectorOptions.filter((o) => !o.value || ids.includes(o.value));
        },
        filteredRoleOptions() {
            if (!this.form.sectorId) return this.roleOptions;
            const ids = this.rolesRaw
                .filter((r) => r.sectorId === this.form.sectorId || r.parentId === this.form.sectorId)
                .map((r) => r.id);
            return this.roleOptions.filter((o) => !o.value || ids.includes(o.value));
        },
        sectionDataLabel() {
            return this.common?.sectionData || this.t.common.sectionData;
        },
    },
    watch: {
        modelValue(open) {
            if (open && this.user) {
                this.form = {
                    boardId: this.user.boardId || null,
                    sectorId: this.user.sectorId || null,
                    roleId: this.user.roleId || null,
                    managerUserId: this.user.managerUserId || null,
                    admissionDate: this.user.admissionDate || null,
                    internalCode: this.user.internalCode || '',
                    status: this.user.status || 'active',
                };
            }
        },
    },
    methods: {
        submit() {
            this.$emit('submit', { ...this.form });
        },
    },
};
</script>

<style scoped>
@import '@/css/weeduc/admin/corporate.css';
</style>
